import { Menu, MenuDivider, MenuItem } from '@szhsin/react-menu';
import { format } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import {
  MenuClassName,
  MenuDividerClassName,
  MenuItemClassName,
  MenuItemIconClassName,
} from '../../../../../DesignSystem/ContextMenu/ContextMenuStyles';
import { ReactComponent as UnlinkIcon } from '../../../../../assets/icons/svg/shared/ic-shared-unlink.svg';
import { ReactComponent as EditIcon } from '../../../../../assets/svg/edit_pen_icon.svg';
import { ReactComponent as MoreIcon } from '../../../../../assets/svg/invoices-icons/icon-three-dots.svg';
import { getBase64Image } from '../../../../../helpers/image.helpers';
import { LinkedAccount } from '../../../hooks/useLinkedAccounts';
import { ReactComponent as BankIcon } from '../../../../../assets/icons/svg/colored-icons/ic-colored-bank-placeholder.svg';

interface ManageCardsItemProps {
  linkedCard: LinkedAccount;
  handleUnlink: (card: LinkedAccount) => void;
  handleRelinkExpiredCard: (card: LinkedAccount) => void;
  selectedCard: LinkedAccount;
}

interface ExpirerdCardContentProps {
  handleUnlink: (card: LinkedAccount) => void;
  selectedCard: LinkedAccount;
  handleRelinkExpiredCard: (card: LinkedAccount) => void;
}

const ExpirerdCardContent = (props: ExpirerdCardContentProps) => {
  const { handleRelinkExpiredCard, handleUnlink, selectedCard } = props;
  const { t } = useTranslation();
  return (
    <div className='cursor-pointer rounded-full p-[2px] relative'>
      <div className='relative'>
        <Menu menuButton={<MoreIcon />} className={MenuClassName} transition direction='left' align='center' gap={8}>
          <MenuItem className={MenuItemClassName} onClick={() => handleRelinkExpiredCard(selectedCard)}>
            <>
              <EditIcon className={MenuItemIconClassName} />
              {t('TRANSACTIONS_FACELIFT.SYNC_CARD.RECONNECT')}
            </>
          </MenuItem>
          <MenuDivider className={MenuDividerClassName} />
          <MenuItem className={`${MenuItemClassName} text-red02`} onClick={() => handleUnlink(selectedCard)}>
            <>
              <UnlinkIcon className={`${MenuItemIconClassName} text-red02`} />
              {t('TRANSACTIONS_FACELIFT.SYNC_CARD.UNLINK_ACCOUNT')}
            </>
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
};

interface CardContentProps {
  handleUnlink: (card: LinkedAccount) => void;
  linkedCard: LinkedAccount;
}

const CardContent = (props: CardContentProps) => {
  const { linkedCard, handleUnlink } = props;
  const { t } = useTranslation();

  return (
    <>
      <div
        className='cursor-pointer rounded-full p-[5px]'
        data-tooltip-id='unlink-tooltip'
        data-tooltip-content={t('TRANSACTIONS_FACELIFT.SYNC_CARD.ULINK')}
        data-tooltip-place='bottom'
      >
        <UnlinkIcon onClick={() => handleUnlink(linkedCard)} />
      </div>
      <Tooltip id='unlink-tooltip' />
    </>
  );
};

const CardHeader = ({ linkedCard }: { linkedCard: LinkedAccount }) => {
  const { t } = useTranslation();
  return (
    <div className='flex flex-row gap-[8px]'>
      {linkedCard.institutionLogo ? (
        <div className='rounded-full border-[1px] border-bw04 h-[40px] w-[40px] p-[4px] flex flex-col'>
          <img src={getBase64Image(linkedCard.institutionLogo)} alt={linkedCard.bankName} />
        </div>
      ) : (
        <BankIcon className='w-[40px] h-[40px]' />
      )}
      <div className='flex flex-col'>
        <div className='font-metricRMedium leading-[146%] text-[16px] text-left'>
          {linkedCard.bankName}
          ,
          {linkedCard.cardNumber}
        </div>
        {linkedCard.expired ? (
          <div className='font-metricR text-xxs text-red02'>{t('TRANSACTIONS_FACELIFT.SYNC_CARD.LINK_EXPIRED')}</div>
        ) : (
          <div className='font-metricR text-xxs text-bw07'>
            {t('TRANSACTIONS_FACELIFT.SYNC_CARD.LAST_UPDATE', {
              // eslint-disable-next-line
              date: format(new Date(linkedCard.lastUpdated), `MMM dd, yyyy 'at' hh:mm a`),
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export const ManageCardsItem = (props: ManageCardsItemProps) => {
  const { linkedCard, handleUnlink, handleRelinkExpiredCard, selectedCard } = props;

  return (
    <div className='flex flex-row justify-between items-center  py-[16px] px-[24px] '>
      <CardHeader linkedCard={linkedCard} />
      <div>
        {linkedCard.expired ? (
          <ExpirerdCardContent
            handleRelinkExpiredCard={handleRelinkExpiredCard}
            handleUnlink={handleUnlink}
            selectedCard={selectedCard}
          />
        ) : (
          <CardContent linkedCard={linkedCard} handleUnlink={handleUnlink} />
        )}
      </div>
    </div>
  );
};
